import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { useSortable } from '../shared/use_sortable';
import { fetch_l1 } from '../api';
import { l1_params } from '../overview/params';
import { email_error_view, teacher_rows_view } from './adapters';
import { filter_teacher_rows } from './params';
import PreviewDialog from './preview_dialog';
import TextEditorDialog from './text_editor_dialog';
import './email_logs.css';

/*
 * PROD-7828 R11 — the Email logs tab (/teacher-payments/email-logs). No send capability yet (R12): a
 * teacher list (reusing Overview's /l1 read, D-4: name + slug, no amounts), a Preview action per row,
 * and the editable greeting/intro/footer. props: pay_month, month_label, on_error(e, fallback).
 */
const LOAD_FAILED_TEXT = 'Could not load the teacher list.';
const LIST_CAP = 500; // R11 has no email status/list endpoint yet; R12 replaces this with /email/list.

const COLUMNS = [
  {
    key: 'teacher',
    label: 'Teacher',
    sort_key: 'teacher_name',
    hideInCard: true,
    render: (r) => (
      <div>
        <div>{r.teacher_name}</div>
        <TeacherSlugLine slug={r.teacher_slug} />
      </div>
    )
  },
  {
    key: 'actions',
    label: 'Actions',
    hideInCard: true,
    render: (r, on_preview) => (
      <button
        type="button"
        className="tp-link"
        aria-label={`Preview email for ${r.teacher_name}`}
        onClick={() => on_preview(r)}
      >
        Preview
      </button>
    )
  }
];

const EmailLogsTab = ({ pay_month, month_label, on_error }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [rows, setRows] = useState([]);
  const [error_text, setErrorText] = useState('');
  const [q, setQ] = useState('');
  const [preview_row, setPreviewRow] = useState(null);
  const [editing_text, setEditingText] = useState(false);
  const seq = useRef(0);
  const heading_ref = useRef(null);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    fetch_l1(
      l1_params({ pay_month, sort: { key: 'teacher_name', dir: 'asc' } }, LIST_CAP, 0)
    )
      .then((data) => {
        if (mine !== seq.current) return;
        setRows(teacher_rows_view(data).rows);
        setStatus('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error(e, LOAD_FAILED_TEXT);
          return;
        }
        setErrorText(v.text);
        setStatus('error');
      });
  }, [pay_month, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => filter_teacher_rows(rows, q), [rows, q]);
  const { sort, on_sort, sorted_rows } = useSortable(filtered, COLUMNS, {
    key: 'teacher_name',
    dir: 'asc'
  });

  const columns = COLUMNS.map((c) =>
    c.key === 'actions' ? { ...c, render: (r) => c.render(r, setPreviewRow) } : c
  );

  return (
    <div className="tp-em">
      <div className="tp-card-hd">
        <span
          className="tp-card-hd-title"
          role="heading"
          aria-level={2}
          tabIndex={-1}
          ref={heading_ref}
        >
          Email logs
        </span>
        <span className="tp-card-hd-sub">{month_label}</span>
      </div>

      <div className="tp-banner tp-banner-warn">
        Test mode: payment emails are sent only to the configured test inbox, never to teachers.
      </div>

      <div className="tp-frow">
        <input
          className="tp-input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by teacher name"
          aria-label="Search teachers"
        />
        <div className="tp-frow-right">
          <button type="button" className="tp-btn-out" onClick={() => setEditingText(true)}>
            Edit email text
          </button>
        </div>
      </div>

      {status === 'loading' ? <CustomLoading /> : null}
      {status === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
      {status === 'ready' ? (
        <DataTable
          columns={columns}
          rows={sorted_rows}
          rowKey={(r) => r.teacher_uuid}
          sort={sort}
          on_sort={on_sort}
          emptyText="No teacher payment rows for this month."
          cardTitle={(r) => <b>{r.teacher_name}</b>}
        />
      ) : null}

      {preview_row ? (
        <PreviewDialog
          pay_month={pay_month}
          month_label={month_label}
          teacher_uuid={preview_row.teacher_uuid}
          teacher_name={preview_row.teacher_name}
          on_close={() => setPreviewRow(null)}
          on_focus_lost={() => heading_ref.current && heading_ref.current.focus({ preventScroll: true })}
          on_error={on_error}
        />
      ) : null}

      {editing_text ? (
        <TextEditorDialog
          pay_month={pay_month}
          month_label={month_label}
          on_close={() => setEditingText(false)}
          on_focus_lost={() => heading_ref.current && heading_ref.current.focus({ preventScroll: true })}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default EmailLogsTab;
